import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api";
import "./teachers.css";

export default function EditTeacher() {
  const { id } = useParams();
  const navigate = useNavigate();
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [teacher, setTeacher] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sectionsMap, setSectionsMap] = useState({});
  const [subjectsMap, setSubjectsMap] = useState({});
  const [assignments, setAssignments] = useState([]);

  /* ================= LOAD DATA ================= */
const loadTeacher = async () => {
  const res = await API.get(`/school-admin/teachers/${id}/`, {
    headers: { "X-SCHOOL-ID": school.school.id },
  });

  setTeacher(res.data);

  // ✅ LOAD OLD ASSIGNMENTS
  const loadedAssignments =
    (res.data.assignments || []).map((a) => ({
      class_model: a.class_id,
      section: a.section_id,
      subject: a.subject,
      is_class_teacher: a.is_class_teacher,
    })) || [];

  setAssignments(
    loadedAssignments.length
      ? loadedAssignments
      : [
          {
            class_model: "",
            section: "",
            subject: "",
            is_class_teacher: false,
          },
        ]
  );

  // ✅ PRELOAD DROPDOWNS
  for (const a of loadedAssignments) {
    if (a.class_model) {
      await loadSections(a.class_model);
      await loadSubjects(a.class_model);
    }
  }
};

  const loadClasses = async () => {
    const res = await API.get("/school-admin/classes/", {
      headers: { "X-SCHOOL-ID": school.school.id },
    });
    setClasses(res.data.classes || []);
  };

  const loadSections = async (classId) => {
    if (!classId || sectionsMap[classId]) return;

    const res = await API.get(`/school-admin/sections/${classId}/`, {
      headers: { "X-SCHOOL-ID": school.school.id },
    });

    setSectionsMap((prev) => ({
      ...prev,
      [classId]: res.data.sections || [],
    }));
  };

  const loadSubjects = async (classId) => {
    if (!classId || subjectsMap[classId]) return;

    const res = await API.get(
      `/school-admin/subjects/?class_id=${classId}`,
      { headers: { "X-SCHOOL-ID": school.school.id } }
    );

    setSubjectsMap((prev) => ({
      ...prev,
      [classId]: res.data.subjects || [],
    }));
  };

  useEffect(() => {
    loadTeacher();
    loadClasses();
    // eslint-disable-next-line
  }, []);

  if (!teacher) return <p>Loading...</p>;

  /* ================= BASIC INFO ================= */

  const handleChange = (e) => {
    setTeacher({ ...teacher, [e.target.name]: e.target.value });
  };

  /* ================= ASSIGNMENTS ================= */

  const updateAssignment = (index, key, value) => {
    const updated = [...assignments];
    updated[index][key] = value;

    if (key === "class_model") {
      updated[index].section = "";
      updated[index].subject = "";
      loadSections(value);
      loadSubjects(value);
    }

    setAssignments(updated);
  };

  const addAssignment = () => {
    setAssignments([
      ...assignments,
      { class_model: "", section: "", subject: "", is_class_teacher: false },
    ]);
  };

  const removeAssignment = (index) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  /* ================= SUBMIT ================= */

  const updateTeacher = async () => {
    const validAssignments = assignments.filter(
      (a) => a.class_model && a.section && a.subject
    );

    await API.put(
      `/school-admin/teachers/update/${id}/`,
      {
        name: teacher.name,
        email: teacher.email,
        phone: teacher.phone,
        salary: teacher.salary,
        address: teacher.address,
        assignments: validAssignments,
      },
      {
        headers: { "X-SCHOOL-ID": school.school.id },
      }
    );

    alert("✅ Teacher updated successfully");
    navigate("/school-admin/teachers");
  };

  /* ================= UI ================= */

  return (
    <div className="teacher-form-container">
      <h2>Edit Teacher</h2>

      {/* BASIC INFO */}
      <div className="teacher-form">
        <label>Name</label>
        <input name="name" value={teacher.name} onChange={handleChange} />

        <label>Email</label>
        <input
          name="email"
          value={teacher.email || ""}
          onChange={handleChange}
        />

        <label>Phone</label>
        <input
          name="phone"
          value={teacher.phone || ""}
          onChange={handleChange}
        />

        <label>Salary</label>
        <input
          name="salary"
          value={teacher.salary || ""}
          onChange={handleChange}
        />

        <label>Address</label>
        <textarea
          name="address"
          value={teacher.address || ""}
          onChange={handleChange}
        />
      </div>

      {/* ASSIGNMENTS */}
      <div className="assignment-section">
        <h3>
          Teaching Assignments <span className="optional">(Optional)</span>
        </h3>

        {assignments.map((a, idx) => (
          <div key={idx} className="assignment-card">
            <select
              value={a.class_model}
              onChange={(e) =>
                updateAssignment(idx, "class_model", e.target.value)
              }
            >
              <option value="">Select Class</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              value={a.section}
              onChange={(e) =>
                updateAssignment(idx, "section", e.target.value)
              }
            >
              <option value="">Select Section</option>
              {(sectionsMap[a.class_model] || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            <select
              value={a.subject}
              onChange={(e) =>
                updateAssignment(idx, "subject", e.target.value)
              }
              disabled={!subjectsMap[a.class_model]}
            >
              <option value="">Select Subject</option>
              {(subjectsMap[a.class_model] || []).map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={a.is_class_teacher}
                onChange={(e) =>
                  updateAssignment(idx, "is_class_teacher", e.target.checked)
                }
              />
              Class Teacher
            </label>

            <button
              type="button"
              className="btn-remove"
              onClick={() => removeAssignment(idx)}
            >
              ✖
            </button>
          </div>
        ))}

        <button type="button" className="btn-add" onClick={addAssignment}>
          + Add Assignment
        </button>
      </div>

      {/* ACTION */}
      <button className="btn-submit" onClick={updateTeacher}>
        Save Changes
      </button>
    </div>
  );
}